import favoriteDiagnosis from "dto/favoriteDiagnosis.dto";
import useApiRequest from "hooks/useApiRequest";
import useQueryParams from "hooks/useQueryParams";
// import useUmami from "hooks/useUmami";
import { sortBy } from "lodash";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import favoriteDiagnosisService from "services/api/favoriteDiagnosis.service";

const normalizeStr = (value?: string) => {
    return (value ?? "").trim().toLowerCase();
};

interface FavoriteContext {
    favoriteForm: UseFormReturn<{ favoriteList: favoriteDiagnosis[] }>;
    favoriteDiagnosis: favoriteDiagnosis[] | undefined;
    handleFavorite: (value: any) => void;
    isFavorited: (value: any) => boolean | undefined;
    isAscending: boolean;
    sortFavorite: () => void;
}

const FavoriteContext = createContext<FavoriteContext | undefined>(undefined);

export const useFavorite = () => {
    const context = useContext(FavoriteContext);

    if (!context) {
        throw new Error("useFavorite must be used within an FavoriteProvider");
    }

    return context;
};

const FavoriteProvider = (props: { children: JSX.Element }) => {
    const { children } = props;

    const [doctorLicense] = useQueryParams("recorder");

    const [getAll, favoriteDiagnosis] = useApiRequest(
        favoriteDiagnosisService.getAll
    );

    const favoriteForm = useForm<{ favoriteList: favoriteDiagnosis[] }>({
        defaultValues: {
            favoriteList: []
        }
    });

    // const { track } = useUmami();

    const [isAscending, setIsAscending] = useState<boolean>(true);

    const sortFavorite = () => {
        setIsAscending((prev) => !prev);
    };

    const favFetchAll = useCallback(() => {
        if (doctorLicense) {
            getAll(doctorLicense).then((value) => {
                const sortedValue = sortBy(value, "term");
                if (!isAscending) {
                    sortedValue.reverse();
                }

                favoriteForm.reset({
                    favoriteList: sortedValue
                });
            });
        }
    }, [doctorLicense, favoriteForm, getAll, isAscending]);

    useEffect(() => {
        favFetchAll();
    }, [favFetchAll]);

    const handleFavorite = async (value: any) => {
        if (!doctorLicense) return;

        const prevFavoriteDiagnosis: favoriteDiagnosis[] =
            favoriteDiagnosis ?? [];
        const isExisted = prevFavoriteDiagnosis.some(
            (item) => normalizeStr(item.term) === normalizeStr(value.term)
        );

        let newValue: favoriteDiagnosis[];
        if (!isExisted) {
            newValue = [
                ...prevFavoriteDiagnosis,
                { ...value, recorder: undefined, recorderName: undefined }
            ];
        } else {
            newValue = prevFavoriteDiagnosis.filter(
                (item) => normalizeStr(item.term) !== normalizeStr(value.term)
            );
        }

        await favoriteDiagnosisService.update(doctorLicense, newValue);
        favFetchAll();
        // track("add-new-favorite-diagnosis", {
        //     term: value.term,
        //     userId: doctorLicense
        // });
    };

    const isFavorited = (value: any) => {
        return (Array.isArray(favoriteDiagnosis) ? favoriteDiagnosis : []).some(
            (item) => item.term === value.term
        );
    };

    return (
        <FavoriteContext.Provider
            value={{
                favoriteForm,
                favoriteDiagnosis,
                handleFavorite,
                isFavorited,
                isAscending,
                sortFavorite
            }}
        >
            {children}
        </FavoriteContext.Provider>
    );
};

export default FavoriteProvider;
